import { React, useState, useEffect } from "react";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  FormHelperText,
} from "@mui/material";
import { Box } from "@mui/system";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useMutation } from "@apollo/client";
import { useTheme } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";

import LoginImage from "../../../assets/images/delivery.png";
import AppButton from "../../base/AppButton.component";
import { InputValidation } from "../../../utils/InputValidationRegex";
import { LOGIN, UPDATE_PASSWORD } from "../../../graphql/mutation/mutation";
import { useAuth } from "../../../context/Auth.context";
import { useParams } from "react-router-dom";
import { boxChild, signIn, formControl, details } from "./Login.style";
import HocPopup from "../../../hoc/hocPopup";
import { TYPE } from "../../../utils/Utils";
import InformationDialog from "../../base/dialog-body/Information.dialog";
import DialogComponent from "../../base/Dialog.component";

const UpdatePasswordComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const params = useParams();
  const [updateForm, setUpdateForm] = useState({
    password: "",
    confirmPassword: "",
    passwordError: "",
    confirmPasswordError: "",
    showPassword: true,
    showConfirmPassword: true,
  });

  const [dialog, setDialog] = useState({
    open: "",
    bodyType: "",
    body: null,
    title: "",
  });

  const redirectPath = location.state?.path || "/";

  const [updatePassword, { loading, data }] = useMutation(UPDATE_PASSWORD);

  const updateHandler = () => {
    updatePassword({
      variables: {
        passwordInput: {
          user_id: JSON.parse(params?.data).user_id,
          email: JSON.parse(params?.data).email,
          password: updateForm.password,
        },
      },
    });
  };

  const handleChange = (prop) => (event) => {
    setUpdateForm({ ...updateForm, [prop]: event.target.value });
    let errorText;
    switch (prop) {
      case "password":
        if (InputValidation.passwordValidation(event.target.value))
          errorText =
            "password must be at least 6 characters, one upper, one lower, one number, and one special character.";
        if (event.target.value === "") errorText = "";
        setUpdateForm({
          ...updateForm,
          password: event.target.value,
          passwordError: errorText,
        });
        break;
      case "confirmPassword":
        if (
          updateForm.password !== event.target.value &&
          updateForm.password !== ""
        )
          errorText = "passwords are not matching";
        if (InputValidation.passwordValidation(event.target.value))
          errorText = "Invalid Password";
        setUpdateForm({
          ...updateForm,
          confirmPasswordError: errorText,
        });
        if (event.target.value === "") errorText = "";
        setUpdateForm({
          ...updateForm,
          confirmPassword: event.target.value,
          confirmPasswordError: errorText,
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (data?.updatePassword?.status) {
      setDialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "Success",
        body: (
          <InformationDialog
            text={
              "Password updated successfully. Please login with new password"
            }
          />
        ),
      });
    } else if (data?.updatePassword?.status === false) {
      setDialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "Unable to update password",
        body: (
          <InformationDialog
            text={"Sorry for inconvenience. Please reach out to dealership"}
          />
        ),
      });
    }
  }, [data]);

  //   const showPasswordHandler = () => {
  //     setUpdateForm({
  //       ...updateForm,
  //       showPassword: !updateForm.showPassword,
  //     });
  //   };
  //   const showConfirmPasswordHandler = () => {
  //     setUpdateForm({
  //       ...updateForm,
  //       showConfirmPassword: !updateForm.showConfirmPassword,
  //     });
  //   };

  const showPasswordHandler = (input) => {
    if (input === "password") {
      setUpdateForm({
        ...updateForm,
        showPassword: !updateForm.showPassword,
      });
    } else if (input === "confirmPassword") {
      setUpdateForm({
        ...updateForm,
        showConfirmPassword: !updateForm.showConfirmPassword,
      });
    }
  };

  return (
    <Box component="form" noValidate autoComplete="off" sx={boxChild}>
      <Box
        component="img"
        sx={{
          flow: 1,
          marginBottom: "40px",
          display: "flex",
          alignSelf: "center",
        }}
        alt="Login"
        src={LoginImage}
        width={160}
      />
      <Typography variant="h6" component="div" sx={signIn}>
        Reset Password
      </Typography>
      <Typography variant="span" component="div" sx={details(theme)}>
        Enter your new password below
      </Typography>
      <FormControl sx={formControl} variant="outlined">
        <InputLabel color={updateForm.emailError ? "error" : "primary"}>
          Password
        </InputLabel>
        <OutlinedInput
          label="Password"
          type={updateForm.showPassword ? "password" : "text"}
          value={updateForm.password}
          onChange={handleChange("password")}
          sx={{ borderRadius: 2 }}
          error={updateForm.passwordError}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => showPasswordHandler("password")}
                onMouseDown={(event) => event.preventDefault()}
                edge="end"
              >
                {updateForm.showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <FormHelperText
          error={updateForm.passwordError}
          sx={{ color: theme.palette.error.main }}
        >
          {updateForm.passwordError || " "}
        </FormHelperText>
      </FormControl>
      <FormControl sx={formControl} variant="outlined">
        <InputLabel color={updateForm.emailError ? "error" : "primary"}>
          Confirm Password
        </InputLabel>
        <OutlinedInput
          label="Confirm Password"
          type={updateForm.showConfirmPassword ? "password" : "text"}
          value={updateForm.confirmPassword}
          onChange={handleChange("confirmPassword")}
          sx={{ borderRadius: 2 }}
          error={updateForm.confirmPasswordError}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => showPasswordHandler("confirmPassword")}
                onMouseDown={(event) => event.preventDefault()}
                edge="end"
              >
                {updateForm.showConfirmPassword ? (
                  <VisibilityOff />
                ) : (
                  <Visibility />
                )}
              </IconButton>
            </InputAdornment>
          }
        />
        <FormHelperText
          error={updateForm.confirmPasswordError}
          sx={{ color: theme.palette.error.main }}
        >
          {updateForm.confirmPasswordError || " "}
        </FormHelperText>
      </FormControl>

      <AppButton
        buttonText="SET PASSWORD"
        sx={{ m: 1 }}
        loading={loading}
        disabled={
          updateForm.password === "" ||
          updateForm.confirmPassword === "" ||
          InputValidation.passwordValidation(updateForm.password) ||
          updateForm.password !== updateForm.confirmPassword
        }
        clickHandler={(event) => {
          loading ? event.preventDefault() : updateHandler();
        }}
      />
      <DialogComponent
        open={dialog.open}
        bodyType={dialog.bodyType}
        body={dialog.body}
        title={dialog.title}
        onClose={() => {
          setDialog({ ...dialog, open: false });
          navigate("/");
        }}
      />
    </Box>
  );
};
export default UpdatePasswordComponent;
